import React, { useMemo, useState } from 'react';
import { Text, Group, Loader, Drawer, Stack, Badge, Divider, useComputedColorScheme } from '@mantine/core';
import useAuthenticatedData from '../../hooks/useAuthenticatedData';
import config from '../../config';
import { DecisionGraph, LegendItem, TYPE_BD, ICON, wrapLabel } from './DecisionCanvas';

/**
 * The DEAL-level decision canvas — the fan-out/fan-in.
 *
 *   Customer brief ──┬──► Line 1  711002-5  SV · quote_now          ──┐
 *                    ├──► Line 2  30-1401-3 SV · quote_with_review  ──┼──► Deal summary
 *                    └──► Line 3  ASP7334   NS · hold_for_sourcing  ──┘
 *
 * WHY THERE ARE TWO CANVASES. DecisionCanvas is drawn for ONE line and may contain nothing computed across
 * lines — a hard contract, because prose about the deal's *other* parts on a screen showing *this* part is
 * exactly the contamination the pipeline works to avoid. But the pipeline genuinely has deal-grain steps, and
 * with only one canvas they had nowhere honest to live: duplicate them onto every line's trace (a lie — they
 * ran once) or omit them (a lie by omission). Omission is what happened, and it is how an AI step became
 * invisible while it kept billing.
 *
 * Deal-grain work is drawn HERE, once, where it is true. Click a line to open that line's own trace.
 */

const X0 = 60, TOPY = 40;
const COL_W = 260, GAP_Y = 24;

const widthOf = (n) => (n?.type === 'line' ? 240 : 220);

/**
 * Three columns: prologue → the line branches → epilogue. The branches stack vertically and the two
 * deal-grain nodes are centred against them, so the fan-out and fan-in read at a glance.
 */
function computeDealLayout(ns) {
  const dim = {};
  ns.forEach((n) => {
    const w = widthOf(n);
    const lines = wrapLabel(n.label, w);
    const subLines = n.summary ? wrapLabel(n.summary, w).length : 0;
    dim[n.id] = { w, h: Math.max(54, 18 + lines.length * 15 + subLines * 12), lines };
  });

  const brief = ns.find((n) => n.id === 'customer_brief');
  const lines = ns.filter((n) => n.type === 'line');
  const tail  = ns.find((n) => n.id === 'deal_summary' || n.id === 'no_deal_summary');

  const pos = {};
  let y = TOPY;
  lines.forEach((n) => { pos[n.id] = { x: X0 + COL_W, y }; y += dim[n.id].h + GAP_Y; });

  // Centre the deal-grain nodes on the stack of branches — the visual claim is "one of these, many of those".
  const stackH = Math.max(0, y - GAP_Y - TOPY);
  const midY = (h) => TOPY + Math.max(0, (stackH - h) / 2);
  if (brief) pos[brief.id] = { x: X0, y: midY(dim[brief.id].h) };
  if (tail)  pos[tail.id]  = { x: X0 + COL_W * 2, y: midY(dim[tail.id].h) };

  ns.filter((n) => !pos[n.id]).forEach((n, k) => { pos[n.id] = { x: X0, y: TOPY + stackH + 120 + k * 80 }; });

  const placed = {};
  ns.forEach((n) => { placed[n.id] = { ...pos[n.id], ...dim[n.id], node: n }; });
  return { placed };
}

export default function DealCanvas({ dealPublicId, onSelectLine }) {
  const scheme = useComputedColorScheme('light');
  const url = dealPublicId
    ? `${config.apiBaseUrl}/api/vesper/deals/${encodeURIComponent(dealPublicId)}/explain/deal`
    : null;
  const { data, isLoading } = useAuthenticatedData(url);
  const [sel, setSel] = useState(null);

  const { placed, edges } = useMemo(() => {
    const ns = data?.nodes || [];
    return { placed: computeDealLayout(ns).placed, edges: data?.edges || [] };
  }, [data]);

  if (isLoading) return <Group justify="center" p="xl"><Loader /></Group>;
  if (!data?.meta?.enriched) return <Text c="dimmed" p="md">Not enriched yet — enrich the deal to see its decision trace.</Text>;

  // Clicking a line branch is the whole point of this canvas: it is the way INTO the line-level trace.
  const onNodeClick = (node) => {
    if (node?.type === 'line' && node.dealLineId != null && onSelectLine) onSelectLine(node.dealLineId);
    else setSel(node);
  };

  return (
    <>
      <Text size="xs" c="dimmed" px="xs" mb={6}>
        The deal as a whole. The <b>customer brief</b> is written once and fed into every line; each line is
        then scored, priced, summarized and judged <b>on its own</b>, knowing nothing about its siblings; the
        <b> deal summary</b> synthesizes them back together at the end. Click a line to see its full trace.
      </Text>
      <Group gap="lg" mb="xs" px="xs" wrap="wrap">
        <LegendItem color={TYPE_BD.ai} label="AI stage (deal-level)" icon={ICON.ai} bold />
        <LegendItem color={TYPE_BD.line} label="Line — click to open its trace" icon={ICON.line} bold />
        <LegendItem color={TYPE_BD.note} label="Note" icon={ICON.note} />
      </Group>
      <DecisionGraph placed={placed} edges={edges} onNodeClick={onNodeClick} />
      {/* Same title treatment as the line-level drawer (DecisionCanvas.NodeDrawer). A bare string here inherits
          Mantine's default heading colour, which is near-black — invisible against the dark-mode drawer. */}
      <Drawer opened={!!sel} onClose={() => setSel(null)} position="right" size="lg"
        title={<Text fw={600} c={scheme === 'dark' ? 'gray.1' : 'dark.8'}>{sel?.label}</Text>}>
        {sel && (
          <Stack gap="sm">
            {sel.model && <Group gap="xs"><Badge color="violet" variant="light">{sel.model}</Badge></Group>}
            {sel.id === 'customer_brief' && (
              <>
                <Divider label="About the customer" />
                <Text size="xs" c="dimmed">
                  Written from the CUSTOMER record alone — the stage is given no lines at all, so it cannot
                  mention a part. That is why the same brief can sit on every line without misleading anyone.
                </Text>
              </>
            )}
            {sel.id === 'deal_summary' && (
              <>
                <Divider label="About the deal" />
                <Text size="xs" c="dimmed">
                  Written LAST, after every line was judged and the AI Sales Agent priced them — so the headline restates
                  the recommendations the rep will actually see, rather than predicting them. It only exists on
                  multi-line deals, and it is deliberately absent from any single line's trace.
                </Text>
              </>
            )}
            {sel.summary && <Text size="sm">{sel.summary}</Text>}
          </Stack>
        )}
      </Drawer>
    </>
  );
}
